// client/src/components/pages/LexiStar/Commissioning/Certificate.jsx
//
// The Trip Reconciliation Certificate (ST-00188, M3-U1; T4-S15; LS-09 §5.20,
// §8 "certificate layout"). Route /lexistar/commissioning/:id/certificate
// (:id = the parallel run), page permission lexistar.commissioning.manage.
//
// A designed, printable document the customer keeps whether the run met its
// floors or was accepted below them: the numbers, the method, the floors, the
// exceptions, the signatories, the certificate number and the SHA-256 hash of
// its content. `@media print` (certificate.css) prints the document alone on
// A4. The PDF rendering (headless Chromium) is deferred to the reports work
// (m3-contracts scope row); Print → "Save as PDF" covers it until then.
//
// Data: GET /parallel-runs/:id (certificate_id, fallback figures) then
// GET /certificates/:certificate_id ({ number, issued_at, issued_by, content,
// hash, watermark }). The content's figures win; the run's report fills gaps.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';
import { useAuth } from '../../../../context/AuthContext';
import { getParallelRun, getCertificate, pickObject } from '../../../../services/lexistarAssurance';
import { formatMiningDate } from '../../../../utils/dateUtils';
import { LoadingState, ErrorBanner, NoPermissionPage, PageHeader } from '../StatusBoard/atoms';
import { fmtNum } from '../StatusBoard/format';
import { buttonClasses } from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';
import {
  metricRows,
  floorText,
  missSummary,
  METRICS,
  metricPass,
  metricText,
} from './commissioningLogic';
import './certificate.css';

const longDate = (iso) =>
  formatMiningDate(iso, { day: 'numeric', month: 'long', year: 'numeric' }) || '—';

/** The method in plain words (matching rules of m3-contracts §7.1; LS-04 §10, §14). */
export const METHOD_TEXT = [
  'LexiStar’s trips, distances, litres and weighbridge-ticket matches for the window were compared with the customer’s own records, truck by truck. Nothing the customer supplied was used to compute LexiStar’s figures.',
  'Trip sheets were matched to detected trips by truck and start time within two hours on the same lane; tickets by ticket number; fuel issues by truck within one hour; odometer readings by truck and date. Every unmatched record was given a cause.',
  'A rate passes when it is at or above its floor; a distance or fuel difference passes when the mean absolute difference is at or below its floor. Records below 70% confidence were held for review and not counted until a person resolved them.',
];

function Mark() {
  // A plain four-point star in the brand accent — the mark, not a decoration.
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 1.5 L14.2 9.8 L22.5 12 L14.2 14.2 L12 22.5 L9.8 14.2 L1.5 12 L9.8 9.8 Z"
        fill="#45b7aa"
      />
    </svg>
  );
}

function VerdictText({ pass }) {
  if (pass === true) return <span className="trc-verdict">✔ Meets</span>;
  if (pass === false) return <span className="trc-verdict trc-verdict--fail">✖ Below floor</span>;
  return <span className="trc-verdict trc-verdict--na">— Not assessed</span>;
}

/** Normalise certificate content + run into what the document prints. */
export function certificateModel({ certificate, run, tenantName }) {
  const c = certificate?.content || {};
  const report = c.report || run?.report || {};
  const metricsSource = c.numbers || c.metrics || report.metrics || {};
  const floors = { ...(run?.floors || {}), ...(c.floors || {}) };
  const rows = metricRows({ metrics: metricsSource }, floors);
  const misses = c.misses || report.misses || [];
  const override =
    c.override ||
    (run?.override_reason
      ? { reason: run.override_reason, second_actor: run.override_second_actor }
      : null);
  const exceptions = Array.isArray(c.exceptions)
    ? c.exceptions.map((e) =>
        typeof e === 'string' ? e : e.text || e.description || JSON.stringify(e)
      )
    : [];
  const signatories =
    Array.isArray(c.signatories) && c.signatories.length
      ? c.signatories.map((s) => ({
          role: s.role || s.title || 'Signatory',
          name: s.name || s.actor || s.email || '—',
          at: s.at || s.signed_at || null,
        }))
      : [
          {
            role: 'Signed off by',
            name: run?.signed_off_by || certificate?.issued_by || '—',
            at: run?.signed_off_at || certificate?.issued_at,
          },
          ...(override?.second_actor
            ? [
                {
                  role: 'Second authorised user (override)',
                  name: override.second_actor,
                  at: run?.signed_off_at || certificate?.issued_at,
                },
              ]
            : []),
        ];
  return {
    number: certificate?.number || '—',
    issuedAt: certificate?.issued_at || null,
    issuedBy: certificate?.issued_by || null,
    hash: certificate?.hash || null,
    watermark: certificate?.watermark || null,
    organisation: c.organisation || c.tenant_name || tenantName || null,
    runName: c.run?.name || c.name || run?.name || null,
    windowFrom: c.window?.from || c.window_from || report.window?.from || run?.window_from,
    windowTo: c.window?.to || c.window_to || report.window?.to || run?.window_to,
    assets: c.assets ?? report.assets ?? (run?.asset_keys || []).length,
    lanes: c.lanes ?? report.lanes ?? (run?.lane_ids || []).length,
    rows,
    floors,
    perAsset: c.per_asset || report.per_asset || [],
    misses,
    missSummary: missSummary(misses),
    override,
    exceptions,
    signatories,
    methodRef: c.method || report.method || null,
    failing: rows.filter((r) => r.pass === false).map((r) => r.label),
  };
}

export function CertificateDocument({ model }) {
  const m = model;
  return (
    <article className="trc-doc" aria-label={`Trip Reconciliation Certificate ${m.number}`}>
      <header className="trc-head">
        <div>
          <div className="trc-wordmark">
            <Mark />
            LexiStar
          </div>
          <div className="trc-descriptor">trip intelligence for bulk haulage</div>
        </div>
        <dl className="trc-number">
          <dt>Certificate</dt>
          <dd data-testid="trc-number">{m.number}</dd>
          <dt>Issued</dt>
          <dd>{longDate(m.issuedAt)}</dd>
        </dl>
      </header>

      <h1 className="trc-title">Trip Reconciliation Certificate</h1>
      <p className="trc-subtitle">
        {[m.organisation, m.runName].filter(Boolean).join(' · ') || 'Parallel run'}
      </p>

      <dl className="trc-facts">
        <div>
          <dt>From</dt>
          <dd>{longDate(m.windowFrom)}</dd>
        </div>
        <div>
          <dt>To</dt>
          <dd>{longDate(m.windowTo)}</dd>
        </div>
        <div>
          <dt>Trucks</dt>
          <dd>{fmtNum(m.assets)}</dd>
        </div>
        <div>
          <dt>Lanes</dt>
          <dd>{fmtNum(m.lanes)}</dd>
        </div>
      </dl>

      <div
        className={`trc-outcome ${m.failing.length ? 'trc-outcome--override' : ''}`}
        role="status"
      >
        {m.failing.length ? (
          <>
            <strong>Accepted below the floor</strong>
            <span>
              {m.failing.join(', ')} {m.failing.length === 1 ? 'is' : 'are'} below the agreed floor;
              accepted by override (see exceptions).
            </span>
          </>
        ) : (
          <>
            <strong>Every assessed floor met</strong>
            <span>
              LexiStar’s figures agree with the customer’s records within the agreed floors.
            </span>
          </>
        )}
      </div>

      <section className="trc-section" aria-label="The numbers">
        <h2>The numbers</h2>
        <div className="trc-scroll">
          <table className="trc-table">
            <thead>
              <tr>
                <th>Measure</th>
                <th>Result</th>
                <th className="trc-num">Floor</th>
                <th>Verdict</th>
              </tr>
            </thead>
            <tbody>
              {m.rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    {r.label}
                    <small>{r.what}</small>
                  </td>
                  <td>{r.text}</td>
                  <td className="trc-num">{floorText(r, r.floor)}</td>
                  <td>
                    <VerdictText pass={r.pass} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {m.perAsset.length > 0 && (
        <section className="trc-section" aria-label="Per truck">
          <h2>Per truck</h2>
          <div className="trc-scroll">
            <table className="trc-table">
              <thead>
                <tr>
                  <th>Truck</th>
                  {METRICS.map((meta) => (
                    <th key={meta.key} className="trc-num">
                      {meta.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {m.perAsset.map((a) => (
                  <tr key={a.asset_key}>
                    <td>{String(a.asset_key).replace(/^reg:/, '')}</td>
                    {METRICS.map((meta) => {
                      const x = a[meta.key] || a.metrics?.[meta.key];
                      const pass = metricPass(x, meta, m.floors);
                      return (
                        <td
                          key={meta.key}
                          className={`trc-num ${pass === false ? 'trc-verdict--fail' : ''}`}
                        >
                          {pass === false ? '✖ ' : ''}
                          {x ? metricText(x, meta).split(' ·')[0].split(' over')[0] : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="trc-section" aria-label="Method">
        <h2>Method</h2>
        {METHOD_TEXT.map((p) => (
          <p key={p.slice(0, 24)} className="trc-prose">
            {p}
          </p>
        ))}
        {m.methodRef && <p className="trc-ref">References: {m.methodRef}</p>}
      </section>

      <section className="trc-section" aria-label="Floors">
        <h2>Floors agreed for this run</h2>
        <p className="trc-prose">
          {METRICS.map((meta) => `${meta.label} ${floorText(meta, m.floors[meta.key])}`).join(
            ' · '
          )}
        </p>
      </section>

      <section className="trc-section" aria-label="Exceptions">
        <h2>Exceptions</h2>
        {m.misses.length === 0 && !m.override && m.exceptions.length === 0 ? (
          <p className="trc-prose">None. Every record of the customer’s was found.</p>
        ) : (
          <ul className="trc-list">
            {m.missSummary.map((s) => (
              <li key={s.cause}>
                {fmtNum(s.count)} record{s.count === 1 ? '' : 's'} not found —{' '}
                {s.label.toLowerCase()}
              </li>
            ))}
            {m.exceptions.map((e) => (
              <li key={e}>{e}</li>
            ))}
            {m.override && (
              <li>
                Signed off below the floor by override: “{m.override.reason}”
                {m.override.second_actor && (
                  <> — second authorised user {m.override.second_actor}</>
                )}
                .
              </li>
            )}
          </ul>
        )}
      </section>

      <section className="trc-section" aria-label="Signatories">
        <h2>Signatories</h2>
        <div className="trc-signatures">
          {m.signatories.map((s, i) => (
            <div key={`${s.role}-${i}`}>
              <div className="trc-signature" aria-hidden="true" />
              <div className="trc-signature-meta">
                {s.name}
                <span>
                  {s.role}
                  {s.at ? ` · ${longDate(s.at)}` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {m.watermark && <p className="trc-watermark">{m.watermark}</p>}

      <footer className="trc-foot">
        <div>
          Certificate {m.number}
          {m.issuedBy ? ` · issued by ${m.issuedBy}` : ''} · {longDate(m.issuedAt)}
        </div>
        <div>
          Integrity (SHA-256 of the certificate content):{' '}
          <span className="trc-hash" data-testid="trc-hash">
            {m.hash || '—'}
          </span>
        </div>
      </footer>
    </article>
  );
}

const Certificate = () => {
  const { id } = useParams();
  const { hasPermission, tenant } = useAuth();
  const canView = hasPermission('lexistar.commissioning.manage');
  const [state, setState] = useState({ loading: true, error: null, run: null, certificate: null });

  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    (async () => {
      try {
        const run = pickObject(await getParallelRun(id), 'parallel_run', 'run');
        let certificate = run?.certificate || null;
        if (!certificate && run?.certificate_id) {
          certificate = pickObject(await getCertificate(run.certificate_id), 'certificate');
        }
        if (alive) setState({ loading: false, error: null, run, certificate });
      } catch (err) {
        if (alive)
          setState({
            loading: false,
            error: errorMessage(err, 'The certificate could not be loaded.'),
            run: null,
            certificate: null,
          });
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, canView]);

  const header = <PageHeader title="Trip Reconciliation Certificate" sticky={false} />;
  if (!canView)
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view certificates."
      />
    );

  const { loading, error, run, certificate } = state;
  return (
    <div className="min-h-screen bg-slate-100 py-4 sm:py-8 px-3 sm:px-6">
      <div className="trc-noprint max-w-[210mm] mx-auto mb-4 flex flex-wrap items-center justify-between gap-2">
        <Link
          to={`/lexistar/commissioning/${id}?step=signoff`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900"
        >
          <FaArrowLeft size={10} /> Back to the run
        </Link>
        {certificate && (
          <button type="button" onClick={() => window.print()} className={buttonClasses.primary}>
            <FaPrint size={12} /> Print or save as PDF
          </button>
        )}
      </div>
      {loading ? (
        <LoadingState label="Loading the certificate..." />
      ) : error ? (
        <div className="max-w-[210mm] mx-auto">
          <ErrorBanner message={error} />
        </div>
      ) : !certificate ? (
        <div className="max-w-[210mm] mx-auto bg-surface border border-slate-200 rounded-xl p-6 text-sm text-slate-700">
          This run has not been signed off, so no certificate has been issued yet.{' '}
          <Link
            to={`/lexistar/commissioning/${id}?step=signoff`}
            className="text-primary-700 underline"
          >
            Go to sign-off
          </Link>
          .
        </div>
      ) : (
        <div className="trc-print-root">
          <CertificateDocument
            model={certificateModel({ certificate, run, tenantName: tenant?.tenant_name })}
          />
        </div>
      )}
    </div>
  );
};

export default Certificate;
